/**
 * Where a chat's draft waits out a reload: localStorage, one entry per
 * admin and session under `supportChat.draft.v2.<admin id>.<session id>`.
 *
 * The page's in-memory store (useComposerDrafts) stays the source of truth:
 * storage only fills a composer that finds nothing there, and mirrors what
 * the agent has as they write. Text (each tab's), mode and buttons only: a File cannot be
 * stored, and a legacy photo's base64 is too big to be worth it, so the
 * agent attaches the file again after a reload. Every read and write is
 * wrapped: private mode, a full or blocked store, and a value someone else
 * wrote are all "no draft", never an error.
 */

import { ChatMessageActionInput, CHAT_ACTION_KINDS } from '../../hooks/chat/api';
import { useAdminStore } from '../../store/adminStore';

export const DRAFT_KEY_PREFIX = 'supportChat.draft.';
const SCOPED_PREFIX = `${DRAFT_KEY_PREFIX}v2.`;
const authenticatedAdminId = () => useAdminStore.getState().admin.admin?.id ?? '';
/** A draft older than this is not brought back: the chat has moved on. */
export const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
/** Text beyond this is not kept: a reply is short, and the store is shared. */
export const DRAFT_MAX_CHARS = 20000;
/** An entry beyond this (text plus buttons) is not stored at all. */
export const DRAFT_MAX_BYTES = 32000;

export interface StoredDraft {
  /** The words in the tab on screen (`mode`'s). */
  input: string;
  mode: 'reply' | 'note';
  /** The other tab's words, when it has its own (a reply put aside for a note). */
  aside?: string;
  actions: ChatMessageActionInput[];
}

interface Entry extends StoredDraft {
  v: 1;
  /** Epoch ms of the last write: the draft's age. */
  savedAt: number;
}

export const draftKey = (sessionId: string, adminId = authenticatedAdminId()) =>
  `${SCOPED_PREFIX}${encodeURIComponent(adminId)}.${encodeURIComponent(sessionId)}`;

/** Nothing worth a reload: no text on either tab and no buttons (the tab alone is not). */
export const isEmptyDraft = (draft: StoredDraft) =>
  !draft.input && !draft.aside && draft.actions.length === 0;

const storage = (): Storage | null => {
  try {
    return window.localStorage ?? null;
  } catch (_) {
    return null;
  }
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/** A button as the composer would have made it; anything else is dropped. */
const asAction = (value: unknown): ChatMessageActionInput | null => {
  if (!isRecord(value)) return null;
  const { kind, label, value: v, rentalId } = value;
  if (typeof kind !== 'string' || typeof label !== 'string') return null;
  if (!(CHAT_ACTION_KINDS as readonly string[]).includes(kind)) return null;
  return {
    kind: kind as ChatMessageActionInput['kind'],
    label,
    ...(typeof v === 'string' ? { value: v } : v === null ? { value: null } : {}),
    ...(typeof rentalId === 'string'
      ? { rentalId }
      : rentalId === null
      ? { rentalId: null }
      : {}),
  };
};

const parseEntry = (raw: string): Entry | null => {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch (_) {
    return null;
  }
  if (!isRecord(value) || value.v !== 1) return null;
  const { input, mode, aside, actions, savedAt } = value;
  if (typeof savedAt !== 'number' || !Number.isFinite(savedAt)) return null;
  return {
    v: 1,
    savedAt,
    input: typeof input === 'string' ? input : '',
    mode: mode === 'note' ? 'note' : 'reply',
    // Written before a tab kept its own words: none aside.
    ...(typeof aside === 'string' && aside ? { aside } : {}),
    actions: Array.isArray(actions)
      ? actions.map(asAction).filter((a): a is ChatMessageActionInput => a !== null)
      : [],
  };
};

/** Drops the session's stored draft, if any. */
export function clearStoredDraft(
  sessionId: string,
  adminId = authenticatedAdminId()
): void {
  if (!adminId) return;
  try {
    storage()?.removeItem(draftKey(sessionId, adminId));
  } catch (_) {
    // Nothing to clear, or nowhere to clear it from.
  }
}

/**
 * The session's stored draft, or null: none, unreadable, empty, or older
 * than DRAFT_MAX_AGE_MS (which is then dropped).
 */
export function readStoredDraft(
  sessionId: string,
  now = Date.now(),
  adminId = authenticatedAdminId()
): StoredDraft | null {
  if (!adminId || adminId !== authenticatedAdminId()) return null;
  try {
    const raw = storage()?.getItem(draftKey(sessionId, adminId));
    if (!raw) return null;
    const entry = parseEntry(raw);
    if (!entry || now - entry.savedAt > DRAFT_MAX_AGE_MS) {
      clearStoredDraft(sessionId, adminId);
      return null;
    }
    const draft: StoredDraft = {
      input: entry.input,
      mode: entry.mode,
      ...(entry.aside ? { aside: entry.aside } : {}),
      actions: entry.actions,
    };
    return isEmptyDraft(draft) ? null : draft;
  } catch (_) {
    return null;
  }
}

/**
 * Stores the session's draft, or drops the entry for an empty one. Text
 * beyond DRAFT_MAX_CHARS is cut; an entry beyond DRAFT_MAX_BYTES is not
 * stored, and an older one under the key goes with it.
 */
export function writeStoredDraft(
  sessionId: string,
  draft: StoredDraft,
  now = Date.now(),
  adminId = authenticatedAdminId()
): void {
  // An old composer's cleanup or pending send must never write as the new admin.
  if (!adminId || adminId !== authenticatedAdminId()) return;
  if (isEmptyDraft(draft)) {
    clearStoredDraft(sessionId, adminId);
    return;
  }
  try {
    const entry: Entry = {
      v: 1,
      savedAt: now,
      input: draft.input.slice(0, DRAFT_MAX_CHARS),
      mode: draft.mode,
      ...(draft.aside ? { aside: draft.aside.slice(0, DRAFT_MAX_CHARS) } : {}),
      actions: draft.actions,
    };
    const json = JSON.stringify(entry);
    if (json.length > DRAFT_MAX_BYTES) {
      clearStoredDraft(sessionId, adminId);
      return;
    }
    storage()?.setItem(draftKey(sessionId, adminId), json);
  } catch (_) {
    // Full, blocked, or private: the draft lives in memory for now.
  }
}

/** Drops expired/unreadable drafts and legacy drafts with no known author. */
export function pruneStoredDrafts(now = Date.now()): void {
  try {
    const store = storage();
    if (!store) return;
    const stale: string[] = [];
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (!key || !key.startsWith(DRAFT_KEY_PREFIX)) continue;
      const entry = parseEntry(store.getItem(key) ?? '');
      if (
        !key.startsWith(SCOPED_PREFIX) ||
        !entry ||
        now - entry.savedAt > DRAFT_MAX_AGE_MS
      )
        stale.push(key);
    }
    stale.forEach((key) => store.removeItem(key));
  } catch (_) {
    // Nothing to prune, or nowhere to prune it from.
  }
}
